export const HOMEPAGE_FLAGSHIP_IDS = Object.freeze({
  THIRTY_EIGHT_ZERO: 'thirty-eight-zero',
  CHAMPIONS_XI: 'champions-xi',
  TRANSFER_WINDOW_CHALLENGE: 'transfer-window-challenge',
  WORLD_XI_CUP: 'world-xi-cup',
});

export const DEFAULT_HOMEPAGE_FLAGSHIP_ID = HOMEPAGE_FLAGSHIP_IDS.THIRTY_EIGHT_ZERO;

export const HOMEPAGE_EVENT_PRIORITIES = Object.freeze({
  'international-tournament': 90,
  'transfer-deadline': 80,
  'champions-league': 70,
  'international-break': 60,
  'domestic-league': 50,
  'transfer-window': 40,
  'manual-override': 100,
});

const VALID_FLAGSHIP_IDS = new Set(Object.values(HOMEPAGE_FLAGSHIP_IDS));

function toTimestamp(value) {
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}
function normalizeEvent(event) {
  if (!event || typeof event !== 'object') return null;
  if (event.enabled === false || !VALID_FLAGSHIP_IDS.has(event.flagshipId)) return null;

  const startsAt = toTimestamp(event.startsAt);
  const endsAt = toTimestamp(event.endsAt);
  if (startsAt === null || endsAt === null || endsAt <= startsAt) return null;

  const eventType = String(event.eventType || '').trim();
  if (!eventType) return null;

  return {
    ...event,
    eventType,
    priority: Number.isFinite(Number(event.priority))
      ? Number(event.priority)
      : (HOMEPAGE_EVENT_PRIORITIES[eventType] || 0),
    startsAtMs: startsAt,
    endsAtMs: endsAt,
  };
}

function buildFallback(reason = 'no-active-event') {
  return {
    flagshipId: DEFAULT_HOMEPAGE_FLAGSHIP_ID,
    source: 'fallback',
    reason,
    event: null,
  };
}

function getActiveOverride(override, nowMs) {
  const event = normalizeEvent({
    ...override,
    eventType: 'manual-override',
    priority: HOMEPAGE_EVENT_PRIORITIES['manual-override'],
  });

  if (!event || nowMs < event.startsAtMs || nowMs >= event.endsAtMs) return null;
  return event;
}

export function selectHomepageFlagship({ now = new Date(), events = [], override = null } = {}) {
  const nowMs = toTimestamp(now);
  if (nowMs === null) return buildFallback('invalid-now');

  const activeOverride = getActiveOverride(override, nowMs);
  if (activeOverride) {
    return {
      flagshipId: activeOverride.flagshipId,
      source: 'override',
      reason: 'manual-override',
      event: activeOverride,
    };
  }

  const activeEvents = (Array.isArray(events) ? events : [])
    .map(normalizeEvent)
    .filter((event) => event && nowMs >= event.startsAtMs && nowMs < event.endsAtMs)
    .sort((a, b) => (
      b.priority - a.priority
      || b.startsAtMs - a.startsAtMs
      || String(a.id || '').localeCompare(String(b.id || ''))
    ));

  const selected = activeEvents[0];
  if (!selected) return buildFallback();

  return {
    flagshipId: selected.flagshipId,
    source: 'calendar',
    reason: selected.eventType,
    event: selected,
  };
}
